import React, { useEffect, useMemo, useState } from 'react';
import { fetchExamTopics } from '../../api/fetchExam';
import { updatePathPracticeProgress } from '../../api/fetchPath';
import ExamScreen from '../Exam/ExamScreen';
import ExamResult from '../Exam/ExamResult';
import styles from './PathTopicHub.module.css';

const formatName = (code) =>
  String(code || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const PathExams = ({ userId, step, onBack }) => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingResult, setPendingResult] = useState(null);
  const [activeExamId, setActiveExamId] = useState(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchExamTopics({ userId });
        if (!active) return;
        setTopics(Array.isArray(data?.exams) ? data.exams : []);
        setPendingResult(data?.pendingResult || null);
      } catch (_) {
        if (active) setTopics([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [userId]);

  const allowedTopicCodes = useMemo(() => {
    const topicCode = step?.repeat_pool?.topicCode || step?.focus_topic || null;
    return topicCode ? [topicCode] : (step?.topic_codes || []);
  }, [step?.focus_topic, step?.repeat_pool?.topicCode, step?.topic_codes]);

  const filteredTopics = useMemo(() => {
    const allowed = new Set(allowedTopicCodes);
    return topics.filter((topic) => allowed.has(topic.code)).slice(0, 3);
  }, [allowedTopicCodes, topics]);

  useEffect(() => {
    if (!step?.focus_topic || filteredTopics.length === 0 || activeExamId) return;
    const hit = filteredTopics.find((topic) => topic.code === step.focus_topic);
    if (hit) setActiveExamId(hit.id);
  }, [activeExamId, filteredTopics, step?.focus_topic]);

  useEffect(() => {
    if (allowedTopicCodes.length !== 1 || filteredTopics.length !== 1 || activeExamId) return;
    setActiveExamId(filteredTopics[0].id);
  }, [activeExamId, allowedTopicCodes.length, filteredTopics]);

  if (pendingResult) {
    return <ExamResult result={pendingResult} onComplete={() => setPendingResult(null)} />;
  }

  if (activeExamId) {
    return (
      <ExamScreen
        examId={activeExamId}
        userId={userId}
        onComplete={() => {
          setActiveExamId(null);
          onBack?.();
        }}
        onExit={async () => {
          if (step?.id) {
            try {
              await updatePathPracticeProgress({ userId, stepId: step.id, delta: 1 });
            } catch (_) {}
          }
          setActiveExamId(null);
          onBack?.();
        }}
      />
    );
  }

  if (loading) {
    return <div className={styles.loading}>Загрузка...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button className={styles.backButton} onClick={onBack}>Назад</button>
      </div>

      <div className={styles.title}>Экзамен</div>
      <div className={styles.subtitle}>Три фиксированные темы для этого checkpoint-блока.</div>

      <div className={styles.row}>
        {filteredTopics.map((topic) => (
          <button
            className={styles.topicButton}
            key={topic.id}
            onClick={() => setActiveExamId(topic.id)}
          >
            <div className={`${styles.circle} ${styles.circleMuted}`}>
              <div className={styles.circleLabel}>{formatName(topic.code)}</div>
            </div>
            <div className={styles.topicName}>{topic.title || formatName(topic.code)}</div>
          </button>
        ))}
      </div>

      {filteredTopics.length === 0 && <div className={styles.empty}>Подходящие экзамены не найдены</div>}
    </div>
  );
};

export default PathExams;
